import { isNullOrUndefined } from "csi/utility/coreUtility";
import { ReactionStore } from "csi/utility/mobxUtility";
import { flatten, isEmpty, keys, some, values } from "lodash";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { MutableRefObject, createContext, useContext } from "react";
import { DropTargetMonitor, XYCoord } from "react-dnd";
import dndCss from "../styles/dnd.module.css";
import { DndCanDropAtIndexWithType, DndMultiSelectMap, DndMutliSelectItemSet } from "../types";

export interface DndItemWithId {
	id: string;
	globalId?: string;
	order?: number;
}

// only one parent can mean there's just one drag and drop list, so root key is used
export const DROP_ROOT = "__DROP_ROOT__";

// increase or decrease to modify scroll speed
const SCROLL_SPEED_DAMPING_FACTOR = 8;

/**
 * Split rect into 3 sections.
 * Container will scroll when mouse is over the sections.
 * ___________________________
 * |                          |
 * |                          |
 * |     LINEAR_BACKWARD      |
 * |--------------------------| p1
 * |          PAUSE           |
 * |--------------------------| p2
 * |      LINEAR_FORWARD      |
 * |                          |
 * |                          |
 * ----------------------------
 */
function getScrollSpeed(y: number, rect: DOMRect): number {
	const { bottom, height, top } = rect;
	const slice = height / 4;
	const p1 = top + slice + slice / 2;
	const p2 = bottom - slice - slice / 2;

	if (y < p1) {
		return (y - p1) / SCROLL_SPEED_DAMPING_FACTOR;
	} else if (y > p2) {
		return (y - p2) / SCROLL_SPEED_DAMPING_FACTOR;
	}

	return 0;
}

/** add or remove dragging class */
export function handleDragHighlight(
	ref: MutableRefObject<HTMLDivElement | null>,
	isDragging: boolean,
	disableBgColorChangeOnDrag?: boolean,
): void {
	if (disableBgColorChangeOnDrag) {
		return;
	}
	const { current } = ref;
	let el = current;
	let markedEl;
	while (el) {
		if (el.dataset.csiDragHighlightWrapper) {
			markedEl = el;
			break;
		}
		el = el.parentElement as HTMLDivElement;
	}
	if (current) {
		const { classList } = (markedEl || current);
		if (isDragging) {
			classList.add(dndCss.isDragging);
		} else {
			classList.remove(dndCss.isDragging);
		}
	}
}

/** given left or top css property, apply some offset before returning */
export function adjustTopOrLeftDragPreview(prop: string | number, offset: number): string {
	return `${parseFloat(prop as string) + offset}px`;
}

export class Divider {
	rect: DOMRect;
	parentId: string = "";
	index: number = null;
	constructor(rect: DOMRect, index: number, parentId: string = DROP_ROOT) {
		this.index = index;
		this.parentId = parentId;
		this.rect = rect;
	}
}

export class DndStore<T extends DndItemWithId> extends ReactionStore {
	/** function that returns true or false based on drag type, drop index, and drop parent */
	private readonly canDrop: DndCanDropAtIndexWithType<T> = null;

	/** map of canDropAtIndexWithType for each drop type */
	@observable canDropMap: Record<string, Record<number, boolean>> = {};

	/** indicate if default multi select highlight should be disabled */
	readonly disableMultiSelectHighlight: boolean = false;

	/** map of refs to dividers */
	private dividerRefs: Record<string, MutableRefObject<HTMLDivElement[]>> = {};

	/** drag item being dragged */
	@observable.ref dragItem: T = null;

	/** indicate if dragging */
	@observable isDragging: boolean = false;

	/** indicate if dragging is in full screen mode */
	@observable isFullScreen: boolean = false;

	/** indicate if dragging is loading */
	@observable isLoading: boolean = false;

	/** indicate if dragging over target */
	@observable isOverTarget: boolean = false;

	/** last selected item, used for shift-clicking */
	lastSelectedItem: T = null;

	/** list of items being dragged over */
	@observable.ref list: T[] = [];

	/** map of selected items */
	@observable multiSelectMap: DndMultiSelectMap<T> = {};

	/** root scrollable container */
	scrollEl: HTMLElement = null;

	/** the scroll function interval enables scrolling while dragging */
	private scrollFuncInterval: NodeJS.Timeout = null;

	/** scroll position of root scrollable container */
	@observable scrollPosition: number = 0;

	/** currently selected divider */
	@observable.ref selectedDivider: Divider = null;

	/** mouse position */
	@observable.ref private position: XYCoord = null;

	/** list of items being previewed, used for canceling drop */
	@observable.ref previewList: T[] = [];

	/** get client y position */
	get clientY(): number {
		return this.position?.y;
	}

	/** get keys of divider refs */
	get dividerRefKeys(): string[] {
		return keys(this.dividerRefs);
	}

	/** re-calculate dom rectangles of divider refs */
	get flatDividers(): Divider[] | undefined {
		const dividerMap: Record<string, Divider[]> = {};
		const { canDropMap, dividerRefs, dragItem } = this;
		if (dragItem) {
			for (const key in dividerRefs) {
				const dividers: Divider[] = [];
				const ref = dividerRefs[key];
				ref.current.forEach((div, i) => {
					runInAction(() => {
						canDropMap[key] ||= {};
						canDropMap[key][i] ??= this.canDrop?.(dragItem, i, key) ?? true;
					});
					// only add the divider if the user can drop to it
					if (canDropMap[key][i]) {
						const divider: Divider = new Divider(div.getBoundingClientRect(), i, key);
						dividers.push(divider);
					}
					dividerMap[key] = dividers;
				});
			}
			return flatten(values(dividerMap));
		}
	};

	/** get array of multi select items */
	get multiSelectItemArray(): T[] {
		return values(this.multiSelectMap);
	}

	/** get set of multi select items */
	get multiSelectItemSet(): DndMutliSelectItemSet<T> {
		return new Set(values(this.multiSelectMap));
	}

	/** get scroll speed */
	get scrollSpeed(): number {
		const { clientY, scrollEl } = this;
		if (clientY && scrollEl) {
			const rect = scrollEl.getBoundingClientRect();
			return getScrollSpeed(clientY, rect);
		}
		return 0;
	};

	/** get list of parents not droppable */
	@computed get parentsDroppable(): string[] {
		return Object.keys(this.canDropMap)
			.filter((canDropKey) => this.parentIsDroppable(canDropKey))
			.sort();
	}

	/** get list of parents not droppable filter root */
	@computed get parentsDroppableFilterRoot(): string[] {
		return Object.keys(this.canDropMap)
			.filter((canDropKey) => canDropKey !== DROP_ROOT && this.parentIsDroppable(canDropKey))
			.sort();
	}

	/** can drop drag item at index and with parent */
	canDropItemAtIndex = (index: number, id = DROP_ROOT): boolean => {
		return this.isDragging && this.canDrop(this.dragItem, index, id);
	};

	/** only one child container is droppable on drag */
	onlyOneChildContainerIsDroppable = (parentId: string): boolean => {
		const { parentsDroppableFilterRoot } = this;
		return this.isDragging && parentsDroppableFilterRoot.length === 1 && parentsDroppableFilterRoot[0] === parentId;
	};

	/** return whether this parent is droppable */
	parentIsDroppable = (parentId: string): boolean => {
		const { canDropMap } = this;
		// we are not greedy about this
		if (isEmpty(canDropMap)) {
			return true;
		}
		return some(this.canDropMap[parentId], (val) => val);
	};

	/** establish the scroll element */
	establishScrollElement = (el: HTMLElement): void => {
		this.scrollEl = el;
	};

	/** is item selected inside multi select map */
	isItemSelected = (item: T): boolean => {
		return !!this.multiSelectMap[item.globalId];
	};

	/** default drop item, this is used when the user does not have a drop handler */
	@action onDropDefault = (item: T, monitor: DropTargetMonitor, targetIndex: number, parentId = DROP_ROOT): void => {
		const { list } = this;
		const sourceIndex = list.indexOf(item);
		list.splice(sourceIndex, 1);
		list.splice(targetIndex, 0, item);
	};

	/** on hover of dnd list */
	@action onHoverDefault = (item: T, monitor: DropTargetMonitor<T, T>, index?: number, groupId?: string) => {
		if (!isNullOrUndefined(index) && !isNullOrUndefined(groupId)) {
			const { previewList } = this;
			const indexOfAttribute = previewList.indexOf(item);
			previewList.splice(indexOfAttribute, 1);
			previewList.splice(index, 0, item);
		}
	};

	/** select item, represented by a key global id inside the multiSelectMap set to the item */
	@action selectItem = (dragItem: T): void => {
		const { multiSelectMap } = this;
		const { globalId } = dragItem;
		if (multiSelectMap[globalId]) {
			delete multiSelectMap[globalId];
		} else {
			this.lastSelectedItem = dragItem;
			multiSelectMap[globalId] = dragItem;
		}
	};

	/** deselect all items inside multi select map */
	@action deselectAllItems = (): void => {
		this.multiSelectMap = {};
	};

	/** remove selected attribute from multi select map */
	@action removeSelectedAttribute = (item: T): void => {
		const { multiSelectMap } = this;
		const { globalId } = item;
		delete multiSelectMap[globalId];
	};

	/** in async the dnd state could be set to loading */
	@action setIsLoading = (loading: boolean): void => {
		this.isLoading = loading;
	};

	/** set mouse cursor position */
	@action setPosition = (xy: XYCoord): void => {
		this.position = xy;
	};

	/** set divider refs for a given parent id */
	@action setDividerRefs = (parentId: string, dividerRefs?: MutableRefObject<HTMLDivElement[]>): void => {
		if (dividerRefs) {
			this.dividerRefs[parentId] = dividerRefs;
		} else {
			delete this.dividerRefs[parentId];
		}
	};

	/** set is user dragging */
	@action setIsDragging = (dragging: boolean): void => {
		this.isDragging = dragging;
	};

	/** set is user over target */
	@action setIsOverTarget = (over: boolean): void => {
		this.isOverTarget = over;
	};

	/** set the selected divider, equivalent to the one that will end up as the target drop index */
	@action setSelectedDivider = (divider: Divider): void => {
		this.selectedDivider = divider;
	};

	/** find given group and index */
	@action calculateSelectedDivider = (): void => {
		let selectedDivider: Divider = null;
		const { clientY, flatDividers } = this;
		if (clientY && flatDividers?.length) {
			let minDifference: number = Infinity;
			for (const [i, divider] of flatDividers.entries()) {
				const difference = Math.abs(divider.rect.y - clientY);
				if (difference < minDifference) {
					minDifference = difference;
					selectedDivider = divider;
				}
			}
			this.selectedDivider = selectedDivider;
		}
	};

	/** scroll the dnd container up or down, for use while the user is dragging */
	@action scrollTheContainer = (): void => {
		const { scrollEl, scrollSpeed } = this;
		scrollEl.scrollBy(0, scrollSpeed);
		this.scrollPosition = scrollEl.scrollTop;
	};

	/** set the internal list of items */
	@action setList = (list: T[]): void => {
		this.list = list;
	};

	/** start drag, this is called when the user clicks on an item */
	@action startDrag = (item: T, isFullScreen: boolean): void => {
		const { list } = this;
		this.previewList = observable.array(list, ({ deep: false }));
		this.dragItem = item;
		this.isFullScreen = isFullScreen;
		this.isDragging = true;
		if (this.scrollEl) {
			clearInterval(this.scrollFuncInterval);
			this.scrollFuncInterval = setInterval(this.scrollTheContainer, 50);
		}
		document.body.classList.add(dndCss.draggingCursorForBody);
	};

	/** end drag, this is called when the user releases the mouse button (or when the drag is cancelled) */
	@action endDrag = (): void => {
		clearInterval(this.scrollFuncInterval);
		this.multiSelectMap = {};
		this.canDropMap = {};
		this.isDragging = false;
		this.isFullScreen = false;
		this.dragItem = null;
		this.position = null;
		this.dividerRefs = {};
		this.selectedDivider = null;
		document.body.classList.remove(dndCss.draggingCursorForBody);
	};

	constructor(
		canDrop?: DndCanDropAtIndexWithType<T>,
		disableMultiSelectHighlight?: boolean,
	) {
		super();
		this.canDrop = canDrop;
		this.disableMultiSelectHighlight = disableMultiSelectHighlight;
		makeObservable(this);
		this.trackReaction(
			reaction(
				() => Math.floor(this.scrollPosition), // small optimizer
				(value) => {
					this.calculateSelectedDivider();
				},
			),
		);
		this.trackReaction(
			reaction(
				() => Math.floor(this.clientY / 10), // small optimizer
				(value) => {
					this.calculateSelectedDivider();
				},
			),
		);
	}
}

export const DndStoreContext = createContext<DndStore<any>>(undefined);
export const useDndStore = () => useContext(DndStoreContext);